import { Dtos } from "@inrev/common";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { DateTime } from "luxon";
import { useRequest } from "../../../utils/request";
import {
	ActionItemByIdMap,
	adminActionItemsQueryKey,
	toRelation,
	transformActionItem,
	transformActionItems,
} from "../action-item.keys";

export const useCreateActionItem = () => {
	const queryClient = useQueryClient();
	const { post } = useRequest();

	const mutation = useMutation({
		mutationFn: async (actionItem: Dtos.ActionItem.Create.Request) =>
			await post<Dtos.ActionItem.Create.Response>("/v2/action-items", actionItem),
		onMutate: async (actionItem) => {
			const relation = toRelation(actionItem);

			await queryClient.cancelQueries({
				queryKey: adminActionItemsQueryKey.actionItemsByRelation(relation),
			});

			const previousActionItems = queryClient.getQueryData<ActionItemByIdMap>(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
			);

			const tempId = crypto.randomUUID() as Dtos.ActionItem.Create.Response["id"];
			const tempActionItem = {
				...actionItem,
				...relation,
				id: tempId,
				createdAt: DateTime.now(),
				updatedAt: DateTime.now(),
			};

			queryClient.setQueryData<ActionItemByIdMap>(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
				(old) => {
					if (old === undefined) return old;
					return { ...old, [tempId]: tempActionItem };
				},
			);

			return { previousActionItems, tempId, relation };
		},
		onError: (_error, _actionItem, context) => {
			if (context === undefined) return;

			queryClient.setQueryData(
				adminActionItemsQueryKey.actionItemsByRelation(context.relation),
				context.previousActionItems,
			);
		},
		onSuccess: (newActionItem, inputData, context) => {
			if (!context) {
				throw new Error("Missing context of previous state");
			}
			const relation = toRelation(inputData);

			queryClient.invalidateQueries({ queryKey: adminActionItemsQueryKey.all(), exact: true });

			queryClient.setQueryData(
				adminActionItemsQueryKey.actionItemsByRelation(relation),
				(old: ActionItemByIdMap | undefined) => {
					if (old === undefined) return transformActionItems([newActionItem]);
					const { [context.tempId]: _temp, ...rest } = old;
					return {
						...rest,
						[newActionItem.id]: transformActionItem(newActionItem),
					};
				},
			);
		},
	});

	return {
		createActionItem: mutation.mutate,
		createActionItemLoading: mutation.isPending,
	};
};
